import React, { useState, useEffect } from 'react';
import { Form, Button, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  useGetStocksQuery,
  useCreateStockMutation,
} from '../../slices/stocksApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';

const StockBulkEditScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editableStocks, setEditableStocks] = useState([]);

  const { data: stocksData, isLoading, error } = useGetStocksQuery();
  const [createStock] = useCreateStockMutation();

  // Load and prepare editable stock data
  useEffect(() => {
    if (stocksData?.stocks) {
      const prepared = stocksData.stocks.map((stock) => ({
        ...stock,
        stockItems: stock.stockItems.map((item) => ({
          ...item,
          qty: Number(item.qty),
          price: Number(item.price),
          total: Number(item.qty) * Number(item.price),
        })),
      }));
      setEditableStocks(prepared);
    }
  }, [stocksData]);

  // Filtered view based on search term
  const filteredStocks = editableStocks.filter(
    (stock) =>
      stock.stockItems.some((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      ) || (stock.note || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle quantity change
  const handleQtyChange = (stockId, itemName, newQty) => {
    setEditableStocks((prev) =>
      prev.map((stock) =>
        stock._id === stockId
          ? {
              ...stock,
              stockItems: stock.stockItems.map((item) =>
                item.name === itemName
                  ? {
                      ...item,
                      qty: Number(newQty),
                      total: Number(newQty) * item.price,
                    }
                  : item
              ),
            }
          : stock
      )
    );
  };

  // Handle price change
  const handlePriceChange = (stockId, itemName, newPrice) => {
    setEditableStocks((prev) =>
      prev.map((stock) =>
        stock._id === stockId
          ? {
              ...stock,
              stockItems: stock.stockItems.map((item) =>
                item.name === itemName
                  ? {
                      ...item,
                      price: Number(newPrice),
                      total: item.qty * Number(newPrice),
                    }
                  : item
              ),
            }
          : stock
      )
    );
  };

  // Export filtered data to Excel
  const exportToExcel = () => {
    const rows = filteredStocks.flatMap((stock) =>
      stock.stockItems
        .filter((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map((item) => ({
          Date: new Date(stock.date).toLocaleDateString(),
          Product: item.name,
          Price: item.price,
          Quantity: item.qty,
          Total: item.total.toFixed(2),
          Note: stock.note || 'N/A',
        }))
    );

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'FilteredStocks');
    XLSX.writeFile(workbook, `FilteredStocks_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Submit updates to backend
  const updateToBackend = async () => {
    try {
      for (const stock of filteredStocks) {
        const updatedItems = stock.stockItems.filter((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (updatedItems.length > 0) {
          await createStock({
            _id: stock._id,
            user: stock.user,
            date: stock.date,
            note: stock.note,
            customerDetails: stock.customerDetails,
            status: stock.status,
            stockItems: updatedItems,
          }).unwrap();
          toast.success(`Updated stock on ${new Date(stock.date).toLocaleDateString()}`);
        }
      }
      toast.success('Matching stock records updated successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update one or more stock records');
    }
  };

  return (
    <>
      <h1>Bulk Edit Stock Records</h1>

      <Form.Group controlId="searchTerm" className="mb-3">
        <Form.Control
          type="text"
          placeholder="Search by product name or note"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Form.Group>

      <div className="mb-3">
        <Button variant="success" className="me-2" onClick={exportToExcel}>
          Export to Excel
        </Button>
        <Button variant="primary" onClick={updateToBackend} disabled={filteredStocks.length === 0}>
          Update Matching Records
        </Button>
      </div>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'Failed to load stock data'}</Message>
      ) : filteredStocks.length === 0 ? (
        <Message variant="info">No matching stock records found</Message>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Date</th>
              <th>Product</th>
              <th>Price</th>
              <th>Qty</th>
              <th>Total</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {filteredStocks.map((stock) =>
              stock.stockItems
                .filter((item) =>
                  item.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((item) => (
                  <tr key={`${stock._id}-${item.name}`}>
                    <td>{new Date(stock.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>
                      <Form.Control
                        type="number"
                        value={item.price}
                        onChange={(e) =>
                          handlePriceChange(stock._id, item.name, e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        value={item.qty}
                        onChange={(e) =>
                          handleQtyChange(stock._id, item.name, e.target.value)
                        }
                      />
                    </td>
                    <td>${item.total.toFixed(2)}</td>
                    <td>{stock.note || 'N/A'}</td>
                  </tr>
                ))
            )}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default StockBulkEditScreen;